import { DOCUMENT } from '@angular/common';
import { DestroyRef, inject, Injectable, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngxs/store';
import { ShortcutTemplate } from '../model/userGuiSettings/guiUserSettings.model';
import { BaseState } from '../state/base/base.state';
import { keyboardEventToShortcutString, normalizedShortcutForComparison } from '../utils/mdps-shortcuts.config';

const TEXT_INPUT_TYPES = new Set(['text', 'search', 'email', 'url', '']);

function stripHtmlToText(html: string): string {
    if (!html || !html.trim()) {
        return '';
    }
    if (typeof DOMParser !== 'undefined') {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const text = doc.body?.textContent ?? '';
        return text.trim();
    }
    return html.replace(/<[^>]*>/g, '').trim();
}

function isEditableTextField(el: Element | null): el is HTMLInputElement | HTMLTextAreaElement {
    if (!el) return false;
    if (el.tagName === 'TEXTAREA') {
        const textarea = el as HTMLTextAreaElement;
        return !textarea.readOnly && !textarea.disabled;
    }
    if (el.tagName === 'INPUT') {
        const input = el as HTMLInputElement;
        const type = (input.type ?? 'text').toLowerCase();
        return TEXT_INPUT_TYPES.has(type) && !input.readOnly && !input.disabled;
    }
    return false;
}

@Injectable({
    providedIn: 'root',
})
export class KeyboardShortcutsGlobalService implements OnDestroy {
    private readonly document = inject(DOCUMENT, { optional: true });
    private readonly store = inject(Store);
    private readonly destroyRef = inject(DestroyRef);
    private shortcutsNone: ShortcutTemplate[] = [];
    private readonly optOutElements = new Set<HTMLElement>();
    private keydownHandler: ((e: KeyboardEvent) => void) | null = null;

    ngOnDestroy(): void {
        if (this.document && this.keydownHandler) {
            this.document.removeEventListener('keydown', this.keydownHandler);
        }
        this.keydownHandler = null;
    }

    init(): void {
        if (this.document && typeof this.document.addEventListener === 'function') {
            this.store
                .select(BaseState.getKeyboardShortcuts)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(all => {
                    this.shortcutsNone = (all ?? []).filter(s => s.type === 'NONE');
                });
            this.keydownHandler = this.handleKeydown.bind(this);
            this.document.addEventListener('keydown', this.keydownHandler, true);
        }
    }

    registerOptOut(el: HTMLElement): void {
        this.optOutElements.add(el);
    }

    unregisterOptOut(el: HTMLElement): void {
        this.optOutElements.delete(el);
    }

    isOptedOut(el: Element | null): boolean {
        if (!el) return false;
        for (const optOut of this.optOutElements) {
            if (optOut === el || optOut.contains(el)) return true;
        }
        return false;
    }

    private handleKeydown(event: KeyboardEvent): void {
        if (this.shortcutsNone.length === 0) {
            return;
        }

        const normalized = keyboardEventToShortcutString(event);
        if (!normalized) return;

        const eventCanonical = normalizedShortcutForComparison(normalized);
        const shortcut = this.shortcutsNone.find(s => s.shortcuts && normalizedShortcutForComparison(s.shortcuts) === eventCanonical);
        if (!shortcut) return;

        const doc = this.document;
        if (!doc) return;

        const activeElement = doc.activeElement;
        if (!activeElement || activeElement.ownerDocument !== doc) return;
        if (!isEditableTextField(activeElement)) return;
        if (this.isOptedOut(activeElement)) return;

        const text = stripHtmlToText(shortcut.content ?? '');
        if (!text) return;

        event.preventDefault();
        event.stopPropagation();
        this.insertAtCursor(activeElement as HTMLInputElement | HTMLTextAreaElement, text);
    }

    private insertAtCursor(el: HTMLInputElement | HTMLTextAreaElement, text: string): void {
        const start = el.selectionStart ?? 0;
        const end = el.selectionEnd ?? start;
        const value = el.value ?? '';
        const newValue = value.slice(0, start) + text + value.slice(end);
        el.value = newValue;
        const newPos = start + text.length;
        el.setSelectionRange(newPos, newPos);
        el.dispatchEvent(new Event('input', { bubbles: true }));
    }
}
